import axios from 'axios';
import router from '@/router';
import { compressImage } from '@/utils/imageCompress';

// ⭐ 从环境变量或配置获取后端地址（不含 /api 前缀）
const getBaseURL = () => {
    // 优先使用环境变量，否则使用硬编码的默认值
    const url = process.env.VUE_APP_API_URL || '/api';
    console.log('🔧 [API] baseURL 配置:', url);
    console.log('🔧 [API] process.env.VUE_APP_API_URL:', process.env.VUE_APP_API_URL);
    console.log('🔧 [API] NODE_ENV:', process.env.NODE_ENV);
    return url;
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  withCredentials: false
});

console.log(' [API] axios baseURL:', api.defaults.baseURL);

// 请求拦截器
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('auth_token');
    const fullUrl = (api.defaults.baseURL || '') + (config.url || '');
    console.log('🚀 [API 请求]', config.method?.toUpperCase(), fullUrl);
    console.log('🚀 [API Token]', token ? token.substring(0, 20) + '...' : '无 token');
    console.log('🚀 [API Params]', JSON.stringify(config.params || {}));
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  error => {
    console.error('❌ [API 请求错误]', error.message);
    return Promise.reject(error);
  }
);

// ✅ 专门的上传接口创建器（绕过响应拦截器）
const createUploadClient = () => {
  return axios.create({
    baseURL: getBaseURL(),
    timeout: 30000,
    withCredentials: false
  });
};

// 响应拦截器 - 兼容两种响应格式
api.interceptors.response.use(
  response => {
    console.log('🟢 响应拦截器 - response.data:', response.data)
    console.log('🟢 响应拦截器 - response.data.code:', response.data?.code)
    
    // ✅ 如果是 blob 类型响应（文件下载），直接返回整个 response
    if (response.config.responseType === 'blob') {
      return response;
    }
    
    // 兼容两种响应格式：
    // 1. { code: 200, message: '...', data: {...} }
    // 2. 直接返回数据对象（Spring Boot REST 风格）
    if (response.data && response.data.code !== undefined) {
      const { code, message, data } = response.data;
      if (code === 200) {
        return data;
      }
      // 返回业务错误
      const error = new Error(message);
      error.code = code;
      return Promise.reject(error);
    }
    // 直接返回数据（Spring Boot REST 响应）
    console.log('🟢 响应拦截器 - 返回 response.data')
    return response.data;
  },
  error => {
    // 处理 HTTP 错误
    if (error.response) {
      const { status, data } = error.response;
      switch (status) {
        case 401: // 未授权（token 过期或无效），清除 token 并跳转到登录
          // 防止多个 401 同时触发多次跳转
          if (router.currentRoute.path !== '/login' && !window.__redirecting_to_login) {
            window.__redirecting_to_login = true;
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_identity');
            localStorage.removeItem('identity');
            localStorage.removeItem('identityId');
            localStorage.removeItem('userId');
            localStorage.removeItem('userInfo');
            // 使用 replace 避免用户返回
            window.location.href = "/login";
          }
          break;
        case 403: // 禁止访问（无权限）
          // 先跳转到 403 页面
          if (router.currentRoute.path !== '/403') {
            router.push('/403');
          }
          break;
        case 404: // 未找到
          break;
        case 500: // 服务器错误
          break;
        default:
          break;
      }
      const errorMessage = data?.message || data?.error || '请求失败';
      const apiError = new Error(errorMessage);
      apiError.status = status;
      return Promise.reject(apiError);
    }
    return Promise.reject(error);
  }
);

// ✅ 专门用于文件上传的方法（不经过响应拦截器）
export const uploadFile = async (url, file, onProgress = null) => {
  // 如果是图片，先压缩
  if (file.type.startsWith('image/')) {
    try {
      file = await compressImage(file);
    } catch (e) {
      console.warn('图片压缩失败，使用原图:', e);
    }
  }

  const formData = new FormData();
  formData.append('file', file);
  
  const token = localStorage.getItem('auth_token');
  const uploadClient = createUploadClient();
  
  console.log('🖼️ 开始上传文件:', file.name);
  
  try {
    const response = await uploadClient.post(url, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // ❌ 不要设置 Content-Type，让浏览器自动设置 multipart/form-data 和 boundary
      },
      onUploadProgress: progressEvent => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`📊 上传进度：${percentCompleted}%`);
          onProgress(percentCompleted);
        }
      }
    });
    
    console.log('📦 完整响应对象 (response):', response);
    console.log('   → status:', response.status);
    console.log('   → data:', response.data);
    console.log('   → response 本身的结构:', Object.keys(response));
    
    // ⭐ 关键：axios 的 response 对象结构是：
    // {
    //   data: { filename: "...", url: "..." },  // ← 后端返回的数据
    //   status: 200,
    //   statusText: "OK",
    //   headers: {...},
    //   config: {...},
    //   request: {...}
    // }
    
    // ✅ 正确获取方式 1：使用 response.data.url
    if (response.data && response.data.url) {
      console.log('✅ 使用 response.data.url 获取成功！');
      console.log('   URL:', response.data.url);
      
      // 返回整个响应体对象（包含 filename 和 url）
      return response.data;
    }
    
    // ✅ 如果 response 本身就是数据（较少见）
    if (response.url) {
      console.log('⚠️ 使用 response.url 获取');
      return response;
    }
    
    throw new Error('无法从响应中获取 url 字段');
    
  } catch (error) {
    console.error('❌ 上传失败:', error);
    throw error;
  }
};

export default api;

// ==================== 订单管理 API ====================

/**
 * 开始打包订单
 */
export const startPacking = (orderId) => {
  return api.post(`/orders/${orderId}/pack`);
};

/**
 * 打包完成
 */
export const completePacking = (orderId, remark = '') => {
  return api.post(`/orders/${orderId}/pack-finish`, null, {
    params: { remark }
  });
};

// ==================== 用户管理 API ====================

/**
 * 分页获取用户列表
 */
export const getUsers = (params = {}) => {
  return api.get('/admin/users', { params });
};

/**
 * 根据 ID 获取用户详情
 */
export const getUserById = (id) => {
  return api.get(`/admin/users/${id}`);
};

/**
 * 创建用户
 */
export const createUser = (data) => {
  return api.post('/admin/users', data);
};

/**
 * 更新用户
 */
export const updateUser = (id, data) => {
  return api.put(`/admin/users/${id}`, data);
};

/**
 * 删除用户
 */
export const deleteUser = (id) => {
  return api.delete(`/admin/users/${id}`);
};

/**
 * 修改用户密码
 */
export const changePassword = (id, data) => {
  return api.put(`/admin/users/${id}/password`, data);
};

/**
 * 分配角色给用户
 */
export const assignRoles = (userId, roleIds) => {
  return api.put(`/admin/users/${userId}/roles`, { roleIds });
};

/**
 * 获取所有角色列表
 */
export const getRoles = () => {
  return api.get('/admin/roles');
};

/**
 * 获取角色的功能点
 */
export const getRoleFeatures = (roleId) => {
  return api.get(`/admin/roles/${roleId}/features`);
};

/**
 * 保存角色功能配置
 */
export const saveRoleFeatures = (roleId, featureCodes) => {
  return api.put(`/admin/roles/${roleId}/features`, featureCodes);
};

/**
 * 获取所有功能点列表
 */
export const getFeatures = (module, enabled) => {
  const params = {};
  if (module) params.module = module;
  if (enabled !== undefined) params.enabled = enabled;
  return api.get('/admin/features', { params });
};

/**
 * 获取用户详情（包含关联的批发商/档口信息）
 */
export const getUserDetail = (userId) => {
  return api.get(`/admin/users/${userId}`);
};

/**
 * 关联用户和批发商
 */
export const linkUserWholesaler = (userId, wholesalerId) => {
  return api.post(`/admin/users/${userId}/link-wholesaler`, null, {
    params: { wholesalerId }
  });
};

/**
 * 获取所有批发商列表
 */
export const getWholesalers = () => {
  return api.get('/wholesalers');
};

/**
 * 关联用户和档口
 */
export const linkUserCustomer = (userId, customerId) => {
  return api.post(`/admin/users/${userId}/link-customer`, null, {
    params: { customerId }
  });
};

/**
 * 获取档口列表
 */
export const getCustomers = (params = {}) => {
  return api.get('/customers', { params });
};

// ==================== 用户关联商户管理 API ====================

/**
 * 获取用户关联的所有商户（批发商 + 档口）
 */
export const getUserMerchants = (userId) => {
  return api.get(`/admin/users/${userId}/merchants`);
};

/**
 * 删除用户关联的批发商
 */
export const unlinkUserWholesaler = (userId, wholesalerId) => {
  return api.delete(`/admin/users/${userId}/merchants/wholesaler/${wholesalerId}`);
};

/**
 * 删除用户关联的档口
 */
export const unlinkUserCustomer = (userId, customerId) => {
  return api.delete(`/admin/users/${userId}/merchants/customer/${customerId}`);
};
